"use client";

import { ChevronDownIcon } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/base/button";
import { Calendar } from "@/components/base/calendar";
import { Input } from "@/components/base/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/base/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/base/select";

interface DateTimePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
}

export function DateTimePicker({ value, onChange }: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false);
  
  // Convert 24-hour time to 12-hour format
  const getTimeComponents = React.useCallback((date?: Date) => {
    if (!date) {
      return { hours: 10, minutes: 0, period: "AM" };
    }
    
    const hours24 = date.getHours();
    const minutes = date.getMinutes();
    const period = hours24 >= 12 ? "PM" : "AM";
    const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
    
    return { hours: hours12, minutes, period };
  }, []);

  const [timeComponents, setTimeComponents] = React.useState(() => getTimeComponents(value));

  // Update time components when the value prop changes
  React.useEffect(() => {
    if (value) {
      const newComponents = getTimeComponents(value);
      setTimeComponents(newComponents);
    }
  }, [value, getTimeComponents]);

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) {
      onChange?.(undefined);
      return;
    }

    // Combine date with current time
    const hours24 = timeComponents.period === "AM" 
      ? (timeComponents.hours === 12 ? 0 : timeComponents.hours)
      : (timeComponents.hours === 12 ? 12 : timeComponents.hours + 12);
    
    const newDateTime = new Date(selectedDate);
    newDateTime.setHours(hours24, timeComponents.minutes, 0, 0);

    onChange?.(newDateTime);
    setOpen(false);
  };

  const updateTime = React.useCallback((newComponents: typeof timeComponents) => {
    setTimeComponents(newComponents);

    if (value) {
      const hours24 = newComponents.period === "AM" 
        ? (newComponents.hours === 12 ? 0 : newComponents.hours)
        : (newComponents.hours === 12 ? 12 : newComponents.hours + 12);
      
      const newDateTime = new Date(value);
      if (isNaN(newDateTime.getTime())) {
        return;
      }
      newDateTime.setHours(hours24, newComponents.minutes, 0, 0);
      onChange?.(newDateTime);
    }
  }, [value, onChange]);

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hours = parseInt(e.target.value);
    if (!isNaN(hours) && hours >= 1 && hours <= 12) {
      updateTime({ ...timeComponents, hours });
    }
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const minutes = parseInt(e.target.value);
    if (!isNaN(minutes) && minutes >= 0 && minutes <= 59) {
      updateTime({ ...timeComponents, minutes });
    }
  };

  const handlePeriodChange = (period: "AM" | "PM") => {
    updateTime({ ...timeComponents, period });
  };

  return (
    <div className="flex gap-2">
      <div className="flex-1">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="justify-between w-full font-normal"
            >
              {value ? value.toLocaleDateString() : "Select date"}
              <ChevronDownIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="overflow-hidden p-0 w-auto" align="start">
            <Calendar
              mode="single"
              selected={value}
              captionLayout="dropdown"
              onSelect={handleDateSelect}
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex gap-1 items-center">
        <Input
          type="number"
          min="1"
          max="12"
          value={timeComponents.hours.toString()}
          onChange={handleHoursChange}
          className="w-16 text-center"
        />
        <span className="text-muted-foreground">:</span>
        <Input
          type="number"
          min="0"
          max="59"
          value={timeComponents.minutes.toString().padStart(2, "0")}
          onChange={handleMinutesChange}
          className="w-16 text-center"
        />
        <Select
          value={timeComponents.period}
          onValueChange={handlePeriodChange}
        >
          <SelectTrigger className="w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AM">AM</SelectItem>
            <SelectItem value="PM">PM</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
